'use client';

import { createContext } from 'react';

import type { BehaviorSubject, Subject } from 'rxjs';

import type { ChannelMessageMediaInfo } from '../../types/ChannelMessageMediaInfo';
import type {
  ExtendedCommunityChannelMessage,
  SendChannelMessageParams,
} from '../../types/ExtendedCommunityChannelMessage';

export interface CommunityMessageState {
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  cursor: string | undefined;
  error: Error | undefined;
}

export interface CommunityMessageContextValue {
  /**
   * The messages subject — newest-first for top-level channel
   * messages, oldest-first for thread replies. Consumers reverse for
   * display when needed.
   */
  messages$: BehaviorSubject<ExtendedCommunityChannelMessage[]>;
  /** Loading / pagination / error state. */
  state$: BehaviorSubject<CommunityMessageState>;
  /**
   * In-flight + recently-uploaded media for the optimistic bubbles.
   * The bubble's attachment view filters by `clientMessageId` to
   * render local previews + progress overlays for its own message.
   */
  medias$: BehaviorSubject<ChannelMessageMediaInfo[]>;
  /** Send a new optimistic message + fire the upload + send pipeline. */
  sendMessage: (params: SendChannelMessageParams) => Promise<void>;
  /**
   * Re-fire the send pipeline for a previously-failed optimistic
   * message. Already-uploaded attachments are reused; only
   * `error`-state ones get re-uploaded. The retry button on the
   * bubble calls this.
   */
  resendMessage: (message: ExtendedCommunityChannelMessage) => Promise<void>;
  /** Drop a failed optimistic message from the local subject. */
  removeMessage: (message: ExtendedCommunityChannelMessage) => void;
  /** Fetch the next (older) page of messages. */
  loadMore: () => void;
  /** Reset + re-fetch from page 1. Used after a channel re-enter. */
  refresh: () => void;
  /**
   * Fires whenever a message's pin state flips or a message is
   * deleted — i.e. whenever the channel's set of pinned messages may
   * have changed. The pinned banner + pinned-messages pane fetch
   * pinned rows through a separate query (the main `messages$`
   * subscription is anchored on the newest seed and never re-delivers
   * pin/delete edits to older messages), so they refetch on this
   * signal to stay in sync. Web port of PopshopRN's
   * `messagePinToggled$` on the channel context.
   */
  pinnedMessagesInvalidated$: Subject<void>;
}

export const CommunityMessageContext =
  createContext<CommunityMessageContextValue | null>(null);
